// SPDX-License-Identifier: BUSL-1.1

/**
 * Shared health-check types + helpers for both dashboards.
 *
 */

/** Shape returned by GET /api/health — mirrors schema-healthcheck.ts output. */
export interface HealthResponse {
  version: string;
  environment: string;
  timestamp: string;
  status: 'ok' | 'degraded' | 'error';
  tables: number;
  missingTables: string[];
  setup_required: boolean;
}

/** Semantic tone for dashboard cards / pills. */
export type HealthTone = 'neutral' | 'ok' | 'warning' | 'error';

/**
 * Map a health response to a dashboard-v1 Card's value + sub + tone.
 * `health=undefined` means the query is still loading; `queryError!=null`
 * means the fetch itself failed (network, 5xx, auth boundary).
 */
export function healthToCardProps(
  health: HealthResponse | undefined,
  queryError: unknown,
): { value: string; sub: string; tone: HealthTone } {
  if (queryError) {
    return {
      value: 'Unreachable',
      sub: 'Health check failed',
      tone: 'error',
    };
  }
  if (!health) {
    return { value: 'Checking…', sub: 'Awaiting health probe', tone: 'neutral' };
  }
  if (health.status === 'ok') {
    return {
      value: 'Healthy',
      sub: `${health.tables} tables · ${health.environment}`,
      tone: 'ok',
    };
  }
  if (health.status === 'degraded') {
    const missing = health.missingTables.slice(0, 2).join(', ') || 'schema drift';
    const more = health.missingTables.length > 2 ? '…' : '';
    return {
      value: 'Degraded',
      sub: `Missing: ${missing}${more}`,
      tone: 'warning',
    };
  }
  // status === 'error'
  return {
    value: 'Unhealthy',
    sub: 'Database unreachable',
    tone: 'error',
  };
}
